import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Badge,
  Flex,
  HStack,
  VStack,
  Text,
  Icon,
  IconButton,
  Spinner,
  Divider,
  Menu,
  MenuButton,
  MenuList,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiCalendar, FiClock, FiUser } from 'react-icons/fi';
import { NavLink } from 'react-router-dom';
import { getTeacherAppointments } from 'services/appointmentBooking';

const STATUS_COLOR = {
  pending:   'yellow',
  confirmed: 'green',
  completed: 'gray',
  cancelled: 'red',
};
const STATUS_LABEL = {
  pending:   'Pendiente',
  confirmed: 'Confirmada',
  completed: 'Completada',
  cancelled: 'Cancelada',
};

const fmtDate = (dateStr) => {
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
};
const fmtTime = (timeStr) => timeStr?.slice(0, 5) ?? '';

function AppointmentItem({ appt, isPast }) {
  const mutedColor = useColorModeValue('gray.400', 'gray.500');
  const textColor  = useColorModeValue(isPast ? 'gray.500' : 'gray.700', isPast ? 'gray.500' : 'gray.200');

  return (
    <Box px={3} py={2} opacity={isPast ? 0.7 : 1}>
      <HStack justify="space-between" mb="2px">
        <HStack spacing={1}>
          <Icon as={FiCalendar} color={isPast ? mutedColor : 'blue.400'} w="11px" h="11px" />
          <Text fontSize="xs" fontWeight="600" color={textColor}>
            {fmtDate(appt.appointment_date)}
          </Text>
          <Icon as={FiClock} color={mutedColor} w="11px" h="11px" ml={1} />
          <Text fontSize="xs" color={textColor}>
            {fmtTime(appt.start_time)}
          </Text>
        </HStack>
        <Badge colorScheme={STATUS_COLOR[appt.status] || 'gray'} fontSize="9px" px={1}>
          {STATUS_LABEL[appt.status] || appt.status}
        </Badge>
      </HStack>
      <HStack spacing={1}>
        <Icon as={FiUser} color={mutedColor} w="11px" h="11px" />
        <Text fontSize="xs" color={textColor} noOfLines={1}>
          {appt.student_name || appt.student_rut}
        </Text>
      </HStack>
    </Box>
  );
}

const REFRESH_OPEN     = 30000;  // 30s cuando el menú está abierto
const REFRESH_BACKGROUND = 120000; // 2 min en background (solo actualiza badge)

export default function AppointmentsWidget({ userId, navbarIcon }) {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading]           = useState(false);
  const [hasLoaded, setHasLoaded]       = useState(false);
  const [isOpen, setIsOpen]             = useState(false);
  const intervalRef                     = useRef(null);
  const listBg  = useColorModeValue('#ffffff', '#1a202c');
  const headBg  = useColorModeValue('#f7fafc', '#2d3748');
  const divider = useColorModeValue('#edf2f7', '#4a5568');

  const load = useCallback(async (silent = false) => {
    if (!userId) return;
    if (!silent) setLoading(true);
    try {
      const res = await getTeacherAppointments(userId);
      if (res?.success) {
        setAppointments(res.data || []);
      }
    } catch (_) {}
    finally {
      if (!silent) setLoading(false);
      setHasLoaded(true);
    }
  }, [userId]);

  // Carga inicial al montar
  useEffect(() => {
    load(true);
  }, [load]);

  // Polling: 30s si abierto, 2 min en background
  useEffect(() => {
    const interval = isOpen ? REFRESH_OPEN : REFRESH_BACKGROUND;
    intervalRef.current = setInterval(() => load(true), interval);
    return () => clearInterval(intervalRef.current);
  }, [isOpen, load]);

  const now      = new Date();
  const upcoming = appointments
    .filter(a => new Date(`${a.appointment_date}T${a.start_time}`) >= now)
    .sort((a, b) => new Date(`${a.appointment_date}T${a.start_time}`) - new Date(`${b.appointment_date}T${b.start_time}`))
    .slice(0, 5);

  const past = appointments
    .filter(a => new Date(`${a.appointment_date}T${a.start_time}`) < now)
    .sort((a, b) => new Date(`${b.appointment_date}T${b.start_time}`) - new Date(`${a.appointment_date}T${a.start_time}`))
    .slice(0, 4);

  const upcomingCount = upcoming.length;

  return (
    <Box position="relative" display="inline-flex" mr="16px">
      <Menu
        placement="bottom-end"
        isLazy
        onOpen={() => { setIsOpen(true); load(false); }}
        onClose={() => setIsOpen(false)}
      >
        <MenuButton
          as={IconButton}
          icon={<Icon as={FiCalendar} w="17px" h="17px" />}
          variant="ghost"
          size="sm"
          color={navbarIcon}
          aria-label="Mis citas"
          _hover={{ bg: 'transparent' }}
          _active={{ bg: 'transparent' }}
        />

        <MenuList
        p={0}
        minW="280px"
        maxW="320px"
        boxShadow="xl"
        borderRadius="md"
        overflow="hidden"
        zIndex={9999}
        sx={{
          backdropFilter:       'none !important',
          WebkitBackdropFilter: 'none !important',
          background:           `${listBg} !important`,
          backgroundColor:      `${listBg} !important`,
          opacity:              '1 !important',
        }}
      >
        {/* Header */}
        <Flex
          bg={headBg}
          px={3}
          py={2}
          justify="space-between"
          align="center"
          borderBottom="1px solid"
          borderColor={divider}
        >
          <HStack spacing={2}>
            <Text fontSize="sm" fontWeight="700" color="gray.700">
              Mis Citas Agendadas
            </Text>
            {loading && <Spinner size="xs" color="blue.400" />}
          </HStack>
          <NavLink to="/admin/agenda">
            <Text fontSize="xs" color="blue.500" _hover={{ textDecor: 'underline' }}>
              Ver agenda
            </Text>
          </NavLink>
        </Flex>

        {loading && (
          <Flex justify="center" py={4}>
            <Spinner size="sm" color="blue.400" />
          </Flex>
        )}

        {!loading && hasLoaded && appointments.length === 0 && (
          <Text fontSize="xs" color="gray.400" textAlign="center" py={4}>
            Sin citas agendadas
          </Text>
        )}

        {!loading && upcoming.length > 0 && (
          <>
            <Text fontSize="10px" fontWeight="700" color="blue.500" px={3} pt={2} pb={1} textTransform="uppercase" letterSpacing="wide">
              Próximas
            </Text>
            <VStack spacing={0} align="stretch" divider={<Divider borderColor={divider} />}>
              {upcoming.map(a => <AppointmentItem key={a.id} appt={a} isPast={false} />)}
            </VStack>
          </>
        )}

        {!loading && past.length > 0 && (
          <>
            <Divider borderColor={divider} mt={upcoming.length > 0 ? 1 : 0} />
            <Text fontSize="10px" fontWeight="700" color="gray.400" px={3} pt={2} pb={1} textTransform="uppercase" letterSpacing="wide">
              Anteriores
            </Text>
            <VStack spacing={0} align="stretch" divider={<Divider borderColor={divider} />}>
              {past.map(a => <AppointmentItem key={a.id} appt={a} isPast={true} />)}
            </VStack>
          </>
        )}

        {/* Footer */}
        {!loading && appointments.length > 0 && (
          <Box borderTop="1px solid" borderColor={divider} px={3} py={2}>
            <NavLink to="/admin/agenda">
              <Text fontSize="xs" color="blue.500" textAlign="center" _hover={{ textDecor: 'underline' }}>
                Ver listado completo de citas →
              </Text>
            </NavLink>
          </Box>
        )}
        </MenuList>
      </Menu>

    {/* Badge fuera del Menu para no interferir con el popper */}
    {upcomingCount > 0 && (
      <Badge
        position="absolute"
        top="-2px"
        right="-2px"
        colorScheme="blue"
        borderRadius="full"
        fontSize="9px"
        minW="16px"
        h="16px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        pointerEvents="none"
        zIndex={1}
      >
        {upcomingCount}
      </Badge>
    )}
  </Box>
  );
}
